import React, { useContext } from "react";
import { DataLayerContext } from "./dataLayer/DataLayer";
import { useObservableProperty } from "./observable/useObservableProperty";
import { HomeScreen } from "./screens/home";
import { LoadingScreen } from "./screens/loadingScreen";
import { ConnectingScreen } from "./screens/connectingScreen";
import "./styles.css";
import "./styles/tokens.css";
import "./styles/components.css";
import { PuzzleScreen } from "./screens/puzzleScreen";
import { ReviewPuzzleScreen } from "./screens/reviewPuzzleScreen";
import { SubmittingSolutionScreen } from "./screens/submittingSolutionScreen";
import { PuzzlePointerStateProvider } from "./gameLogic/puzzlePointerStateProvider";
import { PlayerStatsScreen } from "./screens/playerStatsScreen";
import { FriendsScreen } from "./screens/friendsScreen";
import { LeaderboardScreen } from "./screens/leaderboardScreen";
import { FriendPlayScreen } from "./screens/friendPlayScreen";
import { SettingsScreen } from "./screens/settingsScreen";
import { SignInScreen } from "./screens/signInScreen";
import { TestScenariosScreen } from "./screens/testScenariosScreen";
import { authState } from "./auth/authState";
import { OperationsScreen } from "./screens/operationsScreen";
import { MembershipCelebration } from "./screens/components/membershipCelebration";
import { isTestMode } from "./testMode";
import { FeedbackForm } from "./screens/components/feedbackForm";
import "./screens/testModeControls.css";

export function App() {
  const dataLayer = useContext(DataLayerContext);
  const currentScreen = useObservableProperty(dataLayer.currentScreen);
  const currentOverlay = useObservableProperty(dataLayer.currentOverlay);
  const feedbackPrefill = useObservableProperty(dataLayer.feedbackPrefill);
  const premium = useObservableProperty(dataLayer.premium);
  const premiumCelebration = useObservableProperty(dataLayer.premiumCelebration);
  const testPremiumResumeRequestCount = useObservableProperty(
    dataLayer.testPremiumResumeRequestCount,
  );
  const auth = useObservableProperty(authState);
  const screenRootRef = React.useRef<HTMLDivElement>(null);
  const celebrationFocusRef = React.useRef<HTMLElement | null>(null);
  const celebrationFocusFrameRef = React.useRef<number | null>(null);
  const celebrationFocusContextRef = React.useRef(0);
  const wasShowingPremiumCelebrationRef = React.useRef(false);
  const dismissPremiumCelebration = React.useCallback(
    () => dataLayer.clearPremiumCelebration(),
    [dataLayer],
  );

  let screen: React.ReactNode;
  if (auth.status === "loading") {
    screen = <LoadingScreen />;
  } else if (auth.status === "signedOut") {
    screen = <SignInScreen />;
  } else {
    switch (currentScreen) {
      case "Loading":
        screen = <ConnectingScreen mode="load" />;
        break;
      case "Connecting":
        screen = <ConnectingScreen mode="demo" />;
        break;
      case "TestScenarios":
        screen = <TestScenariosScreen></TestScenariosScreen>;
        break;
      case "Home":
        screen = <HomeScreen></HomeScreen>;
        break;
      case "Puzzle":
        if (dataLayer.currentPuzzle.value) {
          screen = (
            <PuzzlePointerStateProvider puzzle={dataLayer.currentPuzzle.value}>
              <PuzzleScreen></PuzzleScreen>
            </PuzzlePointerStateProvider>
          );
        } else {
          screen = <></>;
        }
        break;
      case "SubmittingSolution":
        screen = <SubmittingSolutionScreen></SubmittingSolutionScreen>;
        break;
      case "ReviewPuzzle":
        if (dataLayer.currentPuzzle.value) {
          screen = <ReviewPuzzleScreen puzzle={dataLayer.currentPuzzle.value}></ReviewPuzzleScreen>;
        } else {
          screen = <></>;
        }
        break;
      case "PlayerStats":
        screen = <PlayerStatsScreen></PlayerStatsScreen>;
        break;
      case "Friends":
        screen = <FriendsScreen></FriendsScreen>;
        break;
      case "Leaderboard":
        screen = <LeaderboardScreen></LeaderboardScreen>;
        break;
      case "FriendPlay":
        screen = <FriendPlayScreen></FriendPlayScreen>;
        break;
      case "Settings":
        screen = <SettingsScreen></SettingsScreen>;
        break;
      case "Operations":
        screen = <OperationsScreen />;
        break;
      default:
        screen = <></>;
        break;
    }
  }

  const showPremiumCelebration =
    auth.status === "signedIn" &&
    premiumCelebration !== null &&
    currentOverlay === undefined &&
    currentScreen !== "Loading" &&
    currentScreen !== "Connecting" &&
    currentScreen !== "Puzzle" &&
    currentScreen !== "SubmittingSolution" &&
    currentScreen !== "TestScenarios";
  const showTestModeControls =
    isTestMode() && auth.status === "signedIn" && currentScreen !== "TestScenarios";

  React.useLayoutEffect(() => {
    const screenRoot = screenRootRef.current;
    if (!screenRoot) return;
    const focusContext = ++celebrationFocusContextRef.current;
    if (celebrationFocusFrameRef.current !== null) {
      cancelAnimationFrame(celebrationFocusFrameRef.current);
      celebrationFocusFrameRef.current = null;
    }
    if (showPremiumCelebration) {
      screenRoot.setAttribute("inert", "");
      screenRoot.setAttribute("aria-hidden", "true");
    } else {
      screenRoot.removeAttribute("inert");
      screenRoot.removeAttribute("aria-hidden");
      const activeModal = screenRoot.querySelector<HTMLElement>(
        '[role="dialog"][aria-modal="true"]',
      );
      if (activeModal && !activeModal.contains(document.activeElement)) {
        activeModal
          .querySelector<HTMLElement>(
            'button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])',
          )
          ?.focus({ preventScroll: true });
      }
      const closedIntoUnderlyingScreen =
        wasShowingPremiumCelebrationRef.current &&
        premiumCelebration === null &&
        currentOverlay === undefined;
      if (closedIntoUnderlyingScreen) {
        const target = celebrationFocusRef.current;
        celebrationFocusRef.current = null;
        const closingScreen = currentScreen;
        const closingPlayerId = auth.status === "signedIn" ? auth.identity.playerId : null;
        celebrationFocusFrameRef.current = requestAnimationFrame(() => {
          celebrationFocusFrameRef.current = requestAnimationFrame(() => {
            celebrationFocusFrameRef.current = null;
            const currentRoot = screenRootRef.current;
            const active = document.activeElement;
            const currentAuth = authState.value;
            const targetCanReceiveFocus =
              target?.isConnected &&
              currentRoot?.contains(target) &&
              !target.closest("[inert]") &&
              !target.matches(":disabled") &&
              target.getClientRects().length > 0;
            const fallbackTarget = Array.from(
              currentRoot?.querySelectorAll<HTMLElement>(
                'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [contenteditable="true"], [tabindex]:not([tabindex="-1"])',
              ) ?? [],
            ).find((element) => {
              const style = window.getComputedStyle(element);
              return (
                element.isConnected &&
                !element.closest("[inert]") &&
                element.getClientRects().length > 0 &&
                style.display !== "none" &&
                style.visibility !== "hidden"
              );
            });
            const restoreTarget = targetCanReceiveFocus ? target : fallbackTarget;
            const hasMeaningfulNewFocus =
              active instanceof HTMLElement &&
              active !== document.body &&
              active !== document.documentElement &&
              active !== target;
            if (
              focusContext !== celebrationFocusContextRef.current ||
              !currentRoot ||
              currentRoot.hasAttribute("inert") ||
              dataLayer.premiumCelebration.value !== null ||
              dataLayer.currentOverlay.value !== undefined ||
              dataLayer.currentScreen.value !== closingScreen ||
              currentAuth.status !== "signedIn" ||
              currentAuth.identity.playerId !== closingPlayerId ||
              document.querySelector('[role="dialog"][aria-modal="true"]') !== null ||
              hasMeaningfulNewFocus ||
              !restoreTarget
            ) {
              return;
            }
            restoreTarget.focus({ preventScroll: true });
          });
        });
      }
    }
    wasShowingPremiumCelebrationRef.current = showPremiumCelebration;
  }, [
    showPremiumCelebration,
    currentScreen,
    currentOverlay,
    auth.status === "signedIn" ? auth.identity.playerId : auth.status,
  ]);

  React.useEffect(
    () => () => {
      if (celebrationFocusFrameRef.current !== null) {
        cancelAnimationFrame(celebrationFocusFrameRef.current);
      }
    },
    [],
  );

  return (
    <>
      <div
        ref={screenRootRef}
        className={showTestModeControls ? "testmode-with-controls" : undefined}
        onFocusCapture={(event) => {
          if (
            premiumCelebration === null &&
            currentOverlay === undefined &&
            event.target instanceof HTMLElement
          ) {
            celebrationFocusRef.current = event.target;
          }
        }}
      >
        {screen}
        {showTestModeControls && (
          <div className="testmode-controls" aria-label="Test mode controls">
            <span aria-live="polite">Checks: {testPremiumResumeRequestCount}</span>
            <button onClick={() => void dataLayer.simulateTestAppReturn()}>Simulate return</button>
            <button onClick={() => dataLayer.enterTestMode()}>Scenarios</button>
          </div>
        )}
        <FeedbackForm
          open={currentOverlay === "feedback"}
          onClose={() => dataLayer.closeOverlay()}
          initialSentiment={feedbackPrefill?.sentiment}
          initialMessage={feedbackPrefill?.message}
        />
      </div>
      {showPremiumCelebration && (
        <MembershipCelebration
          celebration={premiumCelebration}
          complimentaryTier={premium.source === "complimentary" ? premium.tier : null}
          onClose={dismissPremiumCelebration}
        />
      )}
    </>
  );
}
